// Upload a signed agreement, or edit the details of one already stored.
//
// An agreement IS its file, so on ADD the file is required and the upload is the save.
// On EDIT the file is fixed: replacing it would silently change what "the signed
// agreement" points at while keeping its title and date — on the one document where
// that substitution would be worst. Re-uploading is the honest path, and the hint says
// so. (Same rule, same reasoning as a business document.)
//
// THE OWNER IS ALWAYS KNOWN, and the form never asks for it. It is the page you are on
// (the retainer screen, the job screen) or the fold you are inside (the client card's
// work tree) — there is no route to this form from a place that does not already know
// which retainer or job the contract belongs to.
//
// It is fixed on edit for a separate and harder reason: the object key names the owner
// the file was uploaded under, so moving the row would leave the bucket and the database
// disagreeing about which engagement — and which client — owns the file.
//
// `api` is the owner's endpoint set (see api/index.js agreementApis), so this same form
// uploads a client's retainer contract and a supplier's job contract without branching.
import { useState } from 'react';
import he from '../i18n/he';
import Modal from './Modal';
import { Field, Textarea } from './Form';
import { DateControl } from './DateField';
import { ErrorText } from './Loader';

export default function AgreementModal({
  agreement, ownerId, api, onClose, onSaved, namePlaceholder = he.agreements.namePlaceholder,
}) {
  const isNew = !agreement.id;
  // The row's own owner wins when editing (it is the one the object key was built from);
  // `ownerId` is the context a NEW upload lands in.
  const target = agreement.retainer_id || agreement.job_id || ownerId;
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: agreement.title || '',
    signed_date: agreement.signed_date || '',
    notes: agreement.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        title: form.title.trim(),
        signed_date: form.signed_date || '',
        notes: form.notes || '',
      };
      if (isNew) await api.add(target, file, body);
      else await api.update(agreement.id, body);
      onSaved();
    } catch (e) {
      setError(e);
      setBusy(false); // stay open so the file selection isn't lost on a failure
    }
  };

  return (
    <Modal
      title={isNew ? he.agreements.add : he.agreements.edit}
      onClose={onClose}
      onSave={submit}
      saveLabel={busy ? he.agreements.uploading : he.common.save}
      saveDisabled={busy || !target || (isNew && !file)}
    >
      {isNew ? (
        <Field label={`${he.agreements.file} *`}>
          <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
        </Field>
      ) : (
        <Field label={he.agreements.file}>
          <span className="file-name" title={agreement.original_name}>{agreement.original_name}</span>
        </Field>
      )}

      <Field label={he.agreements.name}>
        <input
          value={form.title}
          onChange={set('title')}
          placeholder={namePlaceholder}
        />
      </Field>

      <Field label={he.agreements.signedDate}>
        <DateControl
          label={he.agreements.signedDate}
          value={form.signed_date}
          onChange={set('signed_date')}
        />
      </Field>

      <Textarea label={he.agreements.notes} value={form.notes} onChange={set('notes')} />

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
